class QuickOrderPad extends HTMLElement {
  connectedCallback() {
    this.suggestUrl = this.dataset.suggestUrl;
    this.cartAddUrl = this.dataset.cartAddUrl;
    this.cartUrl = this.dataset.cartUrl;
    this.rowsContainer = this.querySelector('[data-qop-rows]');
    this.rowTemplate = this.querySelector('[data-qop-row-template]');
    this.banner = this.querySelector('[data-qop-banner]');
    this.addCartButton = this.querySelector('[data-qop-add-cart]');
    this.totalWrap = this.querySelector('[data-qop-total-wrap]');
    this.totalValue = this.querySelector('[data-qop-total]');
    this.cartRenderer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.suggestCache = new Map();
    this.productCache = new Map();
    this.moneyFormatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD',
    });

    const initialRows = parseInt(this.dataset.initialRows, 10) || 5;
    for (let i = 0; i < initialRows; i++) this.addRow();
    this.updateTotal();

    this.querySelector('[data-qop-add-row]').addEventListener('click', () => this.addRow().querySelector('[data-qop-query]').focus());
    this.addCartButton.addEventListener('click', () => this.addAllToCart());

    this.rowsContainer.addEventListener('input', (event) => {
      const query = event.target.closest('[data-qop-query]');
      if (query) {
        const row = query.closest('[data-qop-row]');
        this.clearRowState(row);
        this.scheduleResolve(row);
        return;
      }
      if (event.target.closest('[data-qop-qty]')) this.updateTotal();
    });
    this.rowsContainer.addEventListener('keydown', (event) => {
      const queryField = event.target.closest('[data-qop-query]');
      if (queryField) {
        const row = queryField.closest('[data-qop-row]');
        const dropdown = row.querySelector('[data-qop-dropdown]');
        const dropdownOpen = dropdown && !dropdown.hidden;

        if (dropdownOpen) {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveDropdownSelection(dropdown, event.key === 'ArrowDown' ? 1 : -1);
            return;
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            const chosen = dropdown.querySelector('[aria-selected="true"]') || dropdown.querySelector('[data-qop-dropdown-item]');
            if (chosen) chosen.click();
            return;
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeDropdown(row);
            return;
          }
        }
      }

      if (event.key !== 'Enter') return;
      const field = event.target.closest('[data-qop-query], [data-qop-qty]');
      if (!field) return;
      event.preventDefault();
      const row = field.closest('[data-qop-row]');

      if (field.matches('[data-qop-query]')) {
        if (field.value.trim() && !row.dataset.variantId) this.resolveNow(row);
        const qty = row.querySelector('[data-qop-qty]');
        qty.focus();
        qty.select();
        return;
      }

      // Enter in the quantity box moves to the next row's product code field
      let next = row.nextElementSibling;
      if (!next) next = this.addRow();
      next.querySelector('[data-qop-query]').focus();
    });
    this.rowsContainer.addEventListener('focusout', (event) => {
      const row = event.target.closest('[data-qop-row]');
      if (!row) return;
      setTimeout(() => {
        if (!row.contains(document.activeElement)) this.closeDropdown(row);
      }, 0);
    });
    this.rowsContainer.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-qop-remove]');
      if (remove) {
        remove.closest('[data-qop-row]').remove();
        if (!this.rowsContainer.querySelector('[data-qop-row]')) this.addRow();
        this.updateTotal();
      }
    });
    this.rowsContainer.addEventListener('change', (event) => {
      const select = event.target.closest('.quick-order-pad__variant-select');
      if (select) {
        const row = select.closest('[data-qop-row]');
        row.dataset.variantId = select.value;
        const option = select.selectedOptions[0];
        row.dataset.sku = option.dataset.sku || '';
        row.dataset.unitPrice = option.dataset.priceCents || '0';
        row.querySelector('.quick-order-pad__result-price').textContent = option.dataset.price;
        const skuEl = row.querySelector('.quick-order-pad__result-sku');
        if (skuEl) skuEl.textContent = option.dataset.sku ? `SKU: ${option.dataset.sku}` : '';
        const stockEl = row.querySelector('[data-qop-stock]');
        if (stockEl) this.setStockStatus(stockEl, option.dataset.available === 'true');
        this.updateTotal();
      }
    });
  }

  addRow() {
    const row = this.rowTemplate.content.firstElementChild.cloneNode(true);
    this.rowsContainer.appendChild(row);
    return row;
  }

  scheduleResolve(row) {
    clearTimeout(row._qopTimer);
    row._qopGen = (row._qopGen || 0) + 1;
    row._qopTimer = setTimeout(() => this.resolveRow(row), 400);
  }

  resolveNow(row) {
    clearTimeout(row._qopTimer);
    row._qopGen = (row._qopGen || 0) + 1;
    return this.resolveRow(row);
  }

  async flushPendingResolves() {
    const pending = [];
    for (const row of this.rowsContainer.querySelectorAll('[data-qop-row]')) {
      const query = row.querySelector('[data-qop-query]').value.trim();
      if (query && !row.dataset.variantId) pending.push(this.resolveNow(row));
    }
    await Promise.all(pending);
  }

  clearRowState(row) {
    delete row.dataset.variantId;
    delete row.dataset.sku;
    delete row.dataset.title;
    delete row.dataset.unitPrice;
    row.querySelector('[data-qop-result]').innerHTML = '';
    this.closeDropdown(row);
    this.updateTotal();
  }

  closeDropdown(row) {
    const dropdown = row.querySelector('[data-qop-dropdown]');
    if (!dropdown || dropdown.hidden) return;
    dropdown.hidden = true;
    dropdown.innerHTML = '';
    const query = row.querySelector('[data-qop-query]');
    if (query) query.setAttribute('aria-expanded', 'false');
  }

  moveDropdownSelection(dropdown, direction) {
    const items = [...dropdown.querySelectorAll('[data-qop-dropdown-item]')];
    if (!items.length) return;
    const currentIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
    let nextIndex;
    if (currentIndex === -1) {
      nextIndex = direction > 0 ? 0 : items.length - 1;
    } else {
      items[currentIndex].setAttribute('aria-selected', 'false');
      nextIndex = (currentIndex + direction + items.length) % items.length;
    }
    items[nextIndex].setAttribute('aria-selected', 'true');
    items[nextIndex].scrollIntoView({ block: 'nearest' });
  }

  renderCandidates(row, products) {
    const resultCell = row.querySelector('[data-qop-result]');
    resultCell.innerHTML = '<span class="quick-order-pad__result-notice">Choose a match below</span>';

    const dropdown = row.querySelector('[data-qop-dropdown]');
    dropdown.innerHTML = '';

    for (const product of products) {
      const available = product.variants.filter((v) => v.available);
      const variant = available[0] || product.variants[0];

      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'quick-order-pad__dropdown-item';
      item.setAttribute('data-qop-dropdown-item', '');
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');

      const imageSrc = product.featured_image || (product.images && product.images[0]);
      if (imageSrc) {
        const img = document.createElement('img');
        img.className = 'quick-order-pad__dropdown-thumb';
        img.src = imageSrc;
        img.alt = '';
        img.loading = 'lazy';
        item.appendChild(img);
      }

      const info = document.createElement('span');
      info.className = 'quick-order-pad__dropdown-info';

      const title = document.createElement('span');
      title.className = 'quick-order-pad__dropdown-title';
      title.textContent = product.title;
      info.appendChild(title);

      const meta = document.createElement('span');
      meta.className = 'quick-order-pad__dropdown-meta';
      const metaParts = [];
      if (variant.sku) metaParts.push(`SKU: ${variant.sku}`);
      metaParts.push(this.moneyFormatter.format(variant.price / 100));
      metaParts.push(variant.available ? 'In stock' : 'Out of stock');
      meta.textContent = metaParts.join(' · ');
      info.appendChild(meta);

      item.appendChild(info);
      item.addEventListener('click', () => this.selectCandidate(row, product, variant));
      dropdown.appendChild(item);
    }

    dropdown.hidden = false;
    const query = row.querySelector('[data-qop-query]');
    if (query) query.setAttribute('aria-expanded', 'true');
  }

  selectCandidate(row, product, variant) {
    this.renderMatch(row, product, variant);
    this.closeDropdown(row);
    const qty = row.querySelector('[data-qop-qty]');
    qty.focus();
    qty.select();
  }

  async resolveRow(row) {
    const gen = row._qopGen;
    const stale = () => row._qopGen !== gen || !row.isConnected;
    const query = row.querySelector('[data-qop-query]').value.trim();
    const resultCell = row.querySelector('[data-qop-result]');
    this.clearRowState(row);
    if (!query) return;

    resultCell.innerHTML = '<span class="quick-order-pad__result-notice">Searching…</span>';
    try {
      const products = await this.suggest(query);
      if (stale()) return;
      if (!products.length) {
        resultCell.innerHTML = '';
        const err = document.createElement('div');
        err.className = 'quick-order-pad__result-error';
        err.textContent = `No product found for "${query}"`;
        resultCell.appendChild(err);
        return;
      }

      const qLower = query.toLowerCase();
      let skuMatchProduct = null;
      let skuMatchVariant = null;
      const candidates = [];

      for (const suggestion of products) {
        const product = await this.productJson(suggestion.handle);
        if (stale()) return;
        candidates.push(product);
        const exact = product.variants.find((v) => v.sku && v.sku.toLowerCase() === qLower);
        const prefix = exact || product.variants.find((v) => v.sku && v.sku.toLowerCase().startsWith(qLower));
        if (prefix) {
          skuMatchProduct = product;
          skuMatchVariant = prefix;
          break; // an exact/prefix SKU match is unambiguous — stop looking
        }
      }

      if (stale()) return;

      if (skuMatchProduct) {
        this.renderMatch(row, skuMatchProduct, skuMatchVariant);
      } else if (candidates.length === 1) {
        const product = candidates[0];
        const available = product.variants.filter((v) => v.available);
        const variant = available[0] || product.variants[0];
        this.renderMatch(row, product, variant);
      } else {
        // Multiple name matches and no SKU winner — let the customer pick.
        resultCell.innerHTML = '';
        this.renderCandidates(row, candidates);
      }
    } catch (error) {
      if (stale()) return;
      resultCell.innerHTML = '';
      const err = document.createElement('div');
      err.className = 'quick-order-pad__result-error';
      err.textContent = "Couldn't reach the store — try again";
      resultCell.appendChild(err);
    }
  }

  setStockStatus(stockEl, available) {
    stockEl.className = `quick-order-pad__result-stock quick-order-pad__result-stock--${available ? 'in' : 'out'}`;
    stockEl.textContent = available ? 'In stock' : 'Out of stock';
  }

  renderMatch(row, product, variant) {
    row.dataset.variantId = String(variant.id);
    row.dataset.sku = variant.sku || '';
    row.dataset.title = product.title;
    row.dataset.unitPrice = String(variant.price);

    const resultCell = row.querySelector('[data-qop-result]');
    resultCell.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'quick-order-pad__result-match';

    const imageSrc = product.featured_image || (product.images && product.images[0]);
    if (imageSrc) {
      const img = document.createElement('img');
      img.className = 'quick-order-pad__thumb';
      img.src = imageSrc;
      img.alt = '';
      img.width = 50;
      img.height = 50;
      img.loading = 'lazy';
      wrap.appendChild(img);
    }

    const title = document.createElement('p');
    title.className = 'quick-order-pad__result-title';
    title.textContent = product.title;
    wrap.appendChild(title);

    const sku = document.createElement('p');
    sku.className = 'quick-order-pad__result-sku';
    sku.textContent = variant.sku ? `SKU: ${variant.sku}` : '';
    wrap.appendChild(sku);

    const stock = document.createElement('p');
    stock.setAttribute('data-qop-stock', '');
    wrap.appendChild(stock);
    this.setStockStatus(stock, variant.available);

    const price = document.createElement('p');
    price.className = 'quick-order-pad__result-price';
    price.textContent = this.moneyFormatter.format(variant.price / 100);
    wrap.appendChild(price);

    if (product.variants.length > 1 && (!variant.sku || variant.sku.toLowerCase() !== row.querySelector('[data-qop-query]').value.trim().toLowerCase())) {
      const select = document.createElement('select');
      select.className = 'quick-order-pad__variant-select select__select';
      select.setAttribute('aria-label', 'Choose option');
      for (const v of product.variants) {
        const option = document.createElement('option');
        option.value = String(v.id);
        option.textContent = v.available ? v.title : `${v.title} — sold out`;
        option.dataset.sku = v.sku || '';
        option.dataset.price = this.moneyFormatter.format(v.price / 100);
        option.dataset.priceCents = String(v.price);
        option.dataset.available = String(v.available);
        if (v.id === variant.id) option.selected = true;
        select.appendChild(option);
      }
      wrap.appendChild(select);
    }

    resultCell.appendChild(wrap);
    this.updateTotal();
  }

  async suggest(query) {
    const key = query.toLowerCase();
    if (this.suggestCache.has(key)) return this.suggestCache.get(key);
    const params = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[options][limit]': '8' });
    const url = `${this.suggestUrl}.json?${params}&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`suggest ${response.status}`);
    const data = await response.json();
    const products = (data.resources && data.resources.results && data.resources.results.products) || [];
    this.suggestCache.set(key, products);
    return products;
  }

  async productJson(handle) {
    if (this.productCache.has(handle)) return this.productCache.get(handle);
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`product ${response.status}`);
    const product = await response.json();
    this.productCache.set(handle, product);
    return product;
  }

  getRowQuantity(row) {
    let quantity = parseInt(row.querySelector('[data-qop-qty]').value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
    if (quantity > 999) quantity = 999;
    return quantity;
  }

  updateTotal() {
    if (!this.totalWrap) return;
    let cents = 0;
    let hasResolvedRow = false;
    for (const row of this.rowsContainer.querySelectorAll('[data-qop-row]')) {
      if (!row.dataset.variantId || !row.dataset.unitPrice) continue;
      hasResolvedRow = true;
      cents += parseInt(row.dataset.unitPrice, 10) * this.getRowQuantity(row);
    }
    this.totalWrap.hidden = !hasResolvedRow;
    this.totalValue.textContent = this.moneyFormatter.format(cents / 100);
  }

  collectItems() {
    const byVariant = new Map();
    let merged = false;
    for (const row of this.rowsContainer.querySelectorAll('[data-qop-row]')) {
      const variantId = row.dataset.variantId;
      if (!variantId) continue;
      const quantity = this.getRowQuantity(row);
      if (byVariant.has(variantId)) {
        byVariant.get(variantId).quantity += quantity;
        byVariant.get(variantId).rows.push(row);
        merged = true;
      } else {
        byVariant.set(variantId, { id: Number(variantId), quantity, rows: [row] });
      }
    }
    return { items: [...byVariant.values()], merged };
  }

  async addAllToCart() {
    this.addCartButton.disabled = true;
    this.addCartButton.textContent = 'Adding…';

    // Finish resolving any rows the user typed but that are still pending,
    // so quickly-entered codes are never silently skipped.
    await this.flushPendingResolves();

    const { items, merged } = this.collectItems();
    if (!items.length) {
      this.addCartButton.disabled = false;
      this.addCartButton.textContent = 'Add all to cart';
      this.showBanner('error', 'Nothing to add yet — enter a product code in at least one row.');
      return;
    }

    let addedTally = 0;
    const failures = [];
    let lastCartSections = null;

    let before = null;
    try {
      before = (await (await fetch('/cart.js')).json()).item_count;
    } catch (error) {
      before = null;
    }

    for (const item of items) {
      try {
        const body = { items: [{ id: item.id, quantity: item.quantity }] };
        if (this.cartRenderer) {
          body.sections = this.cartRenderer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(`${this.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (response.ok) {
          addedTally += item.quantity;
          for (const row of item.rows) row.remove();
          if (this.cartRenderer) {
            const responseBody = await response.json().catch(() => null);
            if (responseBody && responseBody.sections) lastCartSections = responseBody;
          }
        } else {
          const body = await response.json().catch(() => ({}));
          failures.push(body.description || body.message || 'Could not add this item');
          for (const row of item.rows) {
            const cell = row.querySelector('[data-qop-result]');
            const existing = cell.querySelector('.quick-order-pad__result-error');
            if (existing) existing.remove();
            const err = document.createElement('div');
            err.className = 'quick-order-pad__result-error';
            err.textContent = body.description || body.message || 'Could not add this item';
            cell.appendChild(err);
          }
        }
      } catch (error) {
        failures.push('Network error');
        for (const row of item.rows) {
          const cell = row.querySelector('[data-qop-result]');
          const existing = cell.querySelector('.quick-order-pad__result-error');
          if (existing) existing.remove();
          const err = document.createElement('div');
          err.className = 'quick-order-pad__result-error';
          err.textContent = "Couldn't reach the store — try again";
          cell.appendChild(err);
        }
      }
    }

    let after = null;
    try {
      after = (await (await fetch('/cart.js')).json()).item_count;
    } catch (error) {
      after = null;
    }
    const added = before !== null && after !== null ? after - before : addedTally;

    if (!this.rowsContainer.querySelector('[data-qop-row]')) this.addRow();
    this.addCartButton.disabled = false;
    this.addCartButton.textContent = 'Add all to cart';
    this.updateTotal();

    if (added > 0) {
      if (this.cartRenderer) {
        try {
          // A partial-availability add returns no usable response body, so if none of
          // the successful requests gave us fresh sections, fetch them separately.
          const state = lastCartSections || (await this.fetchCartRendererSections());
          this.cartRenderer.renderContents(state);
          // renderContents() itself doesn't clear this — product-form.js's own
          // add-to-cart flow works around the same gap, so mirror it here too.
          if (this.cartRenderer.classList.contains('is-empty')) this.cartRenderer.classList.remove('is-empty');
        } catch (error) {
          await this.refreshCartBubble();
        }
      } else {
        await this.refreshCartBubble();
      }
    }

    if (failures.length === 0) {
      const notice = merged ? ' (duplicate codes were combined)' : '';
      this.showBanner(
        'success',
        `${added} item${added === 1 ? '' : 's'} added to your cart${notice}. `,
        [
          { href: this.cartUrl, label: 'View cart' },
          { onClick: () => this.keepOrdering(), label: 'Keep ordering' },
        ]
      );
    } else if (added > 0) {
      const notice = merged ? ' (duplicate codes were combined)' : '';
      this.showBanner(
        'error',
        `${added} item${added === 1 ? '' : 's'} added, but ${failures.length} couldn't be — see the rows below${notice}. `,
        [
          { href: this.cartUrl, label: 'View cart' },
          { onClick: () => this.keepOrdering(), label: 'Keep ordering' },
        ]
      );
    } else {
      this.showBanner('error', "Nothing was added — see the rows below.");
    }
  }

  keepOrdering() {
    this.banner.hidden = true;
    const firstQuery = this.rowsContainer.querySelector('[data-qop-query]');
    if (firstQuery) firstQuery.focus();
  }

  showBanner(kind, text, links = []) {
    this.banner.hidden = false;
    this.banner.className = `quick-order-pad__banner quick-order-pad__banner--${kind}`;
    this.banner.textContent = text;
    links.forEach((link, index) => {
      if (index > 0) this.banner.appendChild(document.createTextNode(' '));
      if (link.onClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'link';
        button.textContent = link.label;
        button.addEventListener('click', link.onClick);
        this.banner.appendChild(button);
      } else {
        const a = document.createElement('a');
        a.className = 'link';
        a.href = link.href;
        a.textContent = link.label;
        this.banner.appendChild(a);
      }
    });
  }

  async refreshCartBubble() {
    try {
      const response = await fetch('/?sections=cart-icon-bubble');
      const data = await response.json();
      const doc = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
      const bubble = document.getElementById('cart-icon-bubble');
      const section = doc.querySelector('.shopify-section');
      if (bubble && section) bubble.innerHTML = section.innerHTML;
    } catch (error) {
      /* non-fatal: cart page still shows the right contents */
    }
  }

  async fetchCartRendererSections() {
    const ids = this.cartRenderer.getSectionsToRender().map((section) => section.id);
    const response = await fetch(`/?sections=${ids.join(',')}`);
    if (!response.ok) throw new Error(`sections ${response.status}`);
    return { sections: await response.json() };
  }
}

if (!customElements.get('quick-order-pad')) customElements.define('quick-order-pad', QuickOrderPad);
